/**
 * <burnout-announcement-bar>
 *
 * Rotates its `.bo-announce__item` children, with a segmented progress
 * indicator along the bottom edge — one segment per message, the current one
 * filling in red over the interval.
 *
 * The rotation is driven BY the progress animation rather than by a parallel
 * timer: when the fill animation finishes, we advance. That makes it
 * impossible for the indicator and the actual timing to drift apart, and it
 * makes pause/resume a single call that stops both at once.
 *
 * Pauses on hover (they're reading), on focus-within (they're tabbing through
 * a link), and when the tab is hidden. Arrows and swipe let visitors move
 * through messages themselves.
 *
 * With fewer than two messages nothing runs at all — the first item is already
 * marked active server-side, so a single announcement needs no JS.
 */
class BurnoutAnnouncementBar extends HTMLElement {
  #items = [];
  #fills = [];
  #index = 0;
  #interval = 4000;
  #animation = null;
  #paused = false;
  #pointerDownX = null;

  connectedCallback() {
    this.#items = Array.from(this.querySelectorAll('.bo-announce__item'));
    if (this.#items.length < 2) return;

    // The progress track lives outside this element — it spans the full bar
    // while the message itself stays in a narrower centred column — so scope
    // the lookup to the surrounding bar rather than to `this`.
    const root = this.closest('.bo-announce') ?? this;
    this.#fills = Array.from(root.querySelectorAll('.bo-announce__seg-fill'));
    this.#interval = Number(this.dataset.interval) || 4000;

    // Start from whichever message is actually marked active in the markup
    // rather than assuming the first one, so our index can't begin out of
    // step with what the visitor is looking at.
    const rendered = this.#items.findIndex((item) => item.classList.contains('is-active'));
    this.#index = rendered === -1 ? 0 : rendered;

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.querySelector('[data-bo-prev]')?.addEventListener('click', () => this.#go(-1));
    this.querySelector('[data-bo-next]')?.addEventListener('click', () => this.#go(1));

    // Swipe, for the phones that make up most of the traffic.
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', () => (this.#pointerDownX = null));

    this.#run();
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /**
   * Paint the progress segments for the current index and start the fill
   * animation on the active one. Segments before the current message stay
   * filled, so the bar reads as progress through the whole set.
   */
  #run = () => {
    this.#animation?.cancel();

    this.#fills.forEach((fill, i) => {
      fill.style.transform = i < this.#index ? 'scaleX(1)' : 'scaleX(0)';
    });

    // The animation is the clock: when it finishes, we advance. When the
    // merchant turns the progress indicator off there is no segment to fill,
    // so animate an inert property on the host purely to keep time. One code
    // path either way — otherwise hiding the indicator would stop rotation.
    const active = this.#fills[this.#index];
    const keyframes = active
      ? [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }]
      : [{ opacity: 1 }, { opacity: 1 }];

    this.#animation = (active ?? this).animate(keyframes, {
      duration: this.#interval,
      easing: 'linear',
      fill: 'forwards',
    });

    this.#animation.onfinish = () => this.#go(1);
    if (this.#paused) this.#animation.pause();
  };

  /**
   * Move by `direction` (+1 / -1), wrapping at both ends.
   *
   * Every item is recomputed from the new index rather than mutating just the
   * two that changed, so the visual state can never drift out of sync with
   * `#index` — however the move was triggered.
   */
  #go = (direction) => {
    const previous = this.#items[this.#index];
    this.#index = (this.#index + direction + this.#items.length) % this.#items.length;
    const incoming = this.#items[this.#index];

    // Snap the incoming message to the side it should enter from, without
    // animating. Going forwards it waits below; going backwards, above.
    // Skipping this would slide a returning message the wrong way.
    incoming.style.transition = 'none';
    incoming.classList.remove('is-leaving', 'is-leaving-back');
    if (direction < 0) incoming.classList.add('is-leaving');
    void incoming.offsetHeight; // force reflow so the snap is committed
    incoming.style.transition = '';
    incoming.classList.remove('is-leaving');

    this.#items.forEach((item, i) => {
      const isActive = i === this.#index;
      item.classList.toggle('is-active', isActive);
      item.classList.toggle('is-leaving', item === previous && direction > 0);
      item.classList.toggle('is-leaving-back', item === previous && direction < 0);
      // `inert` keeps links inside hidden messages out of the tab order.
      item.toggleAttribute('inert', !isActive);
    });

    this.#run();
  };

  #pause = () => {
    this.#paused = true;
    this.#animation?.pause();
  };

  #resume = () => {
    this.#paused = false;
    if (!document.hidden) this.#animation?.play();
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #onPointerDown = (event) => {
    this.#pointerDownX = event.clientX;
  };

  #onPointerUp = (event) => {
    if (this.#pointerDownX === null) return;
    const delta = event.clientX - this.#pointerDownX;
    this.#pointerDownX = null;
    if (Math.abs(delta) < 40) return;
    this.#go(delta < 0 ? 1 : -1);
  };
}

if (!customElements.get('burnout-announcement-bar')) {
  customElements.define('burnout-announcement-bar', BurnoutAnnouncementBar);
}
